import { useState, useEffect } from 'react';
import axios from 'axios';
import YouTube from 'react-youtube';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Radio, MapPin, Play, Volume2, Youtube, ExternalLink, Instagram, Facebook } from 'lucide-react';

// TikTok icon component (not available in lucide-react)
const TikTokIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
  </svg>
);

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LiveKirtanPage = () => {
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStream, setSelectedStream] = useState(null);

  useEffect(() => {
    const fetchStreams = async () => {
      try {
        const response = await axios.get(`${API_URL}/live-streams`);
        setStreams(response.data);
        // Auto-select first live stream
        const liveStream = response.data.find(s => s.is_live);
        if (liveStream) setSelectedStream(liveStream);
        else if (response.data.length > 0) setSelectedStream(response.data[0]);
      } catch (error) {
        console.error('Error fetching streams:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStreams();
  }, []);

  const opts = {
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: 1,
      modestbranding: 1,
      rel: 0,
    },
  };

  return (
    <div className="min-h-screen bg-gray-900" data-testid="live-kirtan-page">
      {/* Hero Section with Video */}
      <div className="relative">
        {/* Main Video Player */}
        <div className="aspect-video max-h-[70vh] bg-black">
          {selectedStream ? (
            <YouTube
              videoId={selectedStream.youtube_id}
              opts={opts}
              className="w-full h-full"
              iframeClassName="w-full h-full"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="text-center text-white">
                <Radio className="w-16 h-16 mx-auto mb-4 text-saffron-500 animate-pulse" />
                <h3 className="text-xl font-medium">Loading live streams...</h3>
              </div>
            </div>
          )}
        </div>

        {/* Stream Info Overlay */}
        {selectedStream && (
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
            <div className="max-w-7xl mx-auto flex items-end justify-between">
              <div className="text-white">
                <div className="flex items-center gap-3 mb-2">
                  {selectedStream.is_live && (
                    <Badge className="bg-red-600 text-white animate-pulse">
                      <span className="live-indicator mr-2"></span>
                      LIVE
                    </Badge>
                  )}
                  <Badge className="bg-white/20 backdrop-blur-sm text-white">
                    {selectedStream.gurudwara_name}
                  </Badge>
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold mb-1">
                  {selectedStream.title}
                </h1>
                {selectedStream.location && (
                  <p className="flex items-center gap-2 text-gray-300">
                    <MapPin className="w-4 h-4" />
                    {selectedStream.location}
                  </p>
                )}
              </div>
              
              <div className="hidden md:flex items-center gap-4">
                {selectedStream.youtube_id && (
                  <Button 
                    onClick={() => window.open(`https://www.youtube.com/watch?v=${selectedStream.youtube_id}`, '_blank')}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Youtube className="w-4 h-4 mr-2" />
                    Watch on YouTube
                    <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                )}
                <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  <Volume2 className="w-4 h-4 mr-2" />
                  Audio Only
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Stream Selection */}
      <div className="bg-gray-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
            <h2 className="font-heading text-2xl font-bold text-white">
              Gurudwara Live Streams
            </h2>
            {/* Social Media Buttons */}
            <div className="flex gap-2 flex-wrap">
              <Button 
                onClick={() => window.open('https://www.youtube.com/results?search_query=live+gurdwara+kirtan&sp=EgJAAQ%253D%253D', '_blank')}
                className="bg-red-600 hover:bg-red-700 text-white inline-flex items-center gap-2"
              >
                <Youtube className="w-4 h-4" />
                <span className="hidden sm:inline">YouTube</span>
              </Button>
              <Button 
                onClick={() => window.open('https://www.instagram.com/explore/tags/livekirtan', '_blank')}
                className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 hover:from-purple-600 hover:via-pink-600 hover:to-orange-600 text-white inline-flex items-center gap-2"
              >
                <Instagram className="w-4 h-4" />
                <span className="hidden sm:inline">Instagram</span>
              </Button>
              <Button 
                onClick={() => window.open('https://www.facebook.com/search/videos?q=live%20gurdwara%20kirtan', '_blank')}
                className="bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2"
              >
                <Facebook className="w-4 h-4" />
                <span className="hidden sm:inline">Facebook</span>
              </Button>
              <Button 
                onClick={() => window.open('https://www.tiktok.com/search?q=gurdwara%20kirtan', '_blank')}
                className="bg-black hover:bg-gray-800 text-white inline-flex items-center gap-2"
              >
                <TikTokIcon className="w-4 h-4" />
                <span className="hidden sm:inline">TikTok</span>
              </Button>
            </div>
          </div>
          
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <Card key={i} className="bg-gray-800 border-gray-700 animate-pulse">
                  <div className="aspect-video bg-gray-700"></div>
                  <CardContent className="p-4">
                    <div className="h-5 bg-gray-700 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-700 rounded w-1/2"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {streams.map((stream) => (
                <Card 
                  key={stream.id}
                  className={`bg-gray-800 border-gray-700 overflow-hidden cursor-pointer transition-all hover:border-saffron-500 ${
                    selectedStream?.id === stream.id ? 'ring-2 ring-saffron-500' : ''
                  }`}
                  onClick={() => setSelectedStream(stream)}
                  data-testid={`stream-card-${stream.id}`}
                >
                  <div className="aspect-video relative">
                    <img
                      src={`https://img.youtube.com/vi/${stream.youtube_id}/mqdefault.jpg`}
                      alt={stream.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <div className="w-12 h-12 bg-saffron-500 rounded-full flex items-center justify-center">
                        <Play className="w-6 h-6 text-white ml-1" />
                      </div>
                    </div>
                    {stream.is_live && (
                      <Badge className="absolute top-2 left-2 bg-red-600 text-white text-xs">
                        LIVE
                      </Badge>
                    )}
                  </div>
                  
                  <CardContent className="p-3">
                    <h3 className="font-medium text-white text-sm line-clamp-1 mb-1">
                      {stream.gurudwara_name}
                    </h3>
                    {stream.location && (
                      <p className="text-gray-400 text-xs flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {stream.location}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Info Section */}
      <div className="bg-gradient-to-b from-gray-900 to-royal-900 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="space-y-2">
              <div className="w-16 h-16 bg-saffron-500/20 rounded-full flex items-center justify-center mx-auto">
                <Radio className="w-8 h-8 text-saffron-500" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white">24/7 Live</h3>
              <p className="text-gray-400 text-sm">
                Watch live kirtan from Golden Temple and other historic Gurudwaras around the clock.
              </p>
            </div>
            
            <div className="space-y-2">
              <div className="w-16 h-16 bg-saffron-500/20 rounded-full flex items-center justify-center mx-auto">
                <MapPin className="w-8 h-8 text-saffron-500" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white">Global Gurudwaras</h3>
              <p className="text-gray-400 text-sm">
                Access live streams from Gurudwaras in India, UK, USA, Canada, and more.
              </p>
            </div>
            
            <div className="space-y-2">
              <div className="w-16 h-16 bg-saffron-500/20 rounded-full flex items-center justify-center mx-auto">
                <Volume2 className="w-8 h-8 text-saffron-500" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-white">High Quality</h3>
              <p className="text-gray-400 text-sm">
                Experience crystal clear audio and HD video for an immersive spiritual experience.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveKirtanPage;
